import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CloseOutlined, FileImageOutlined, LoadingOutlined } from '@ant-design/icons'

import TranslationNS from '@/utils/Constants/TranslationNS'
import EventHubDialog, {
    EVENT_HUB_DIALOG_BUTTON_CLASS,
    EVENT_HUB_DIALOG_GHOST_CLASS,
    EVENT_HUB_DIALOG_PRIMARY_CLASS,
    EVENT_HUB_DIALOG_Z,
    EventHubDialogActions,
} from './EventHubDialog'
import { readImageDimensions } from './eventHub.utils'
import {
    EVENT_HUB_UPLOAD_KIND,
    EVENT_HUB_UPLOAD_RULES,
    UPLOAD_ISSUE,
    formatUploadBytes,
    getUploadFileIssue,
    getValidUploadFiles,
    isAcceptedUploadType,
} from './eventHub.upload.utils'

/** Title and requirements line per upload kind (mock `UPS[k].title` / `.dims`). */
const KIND_COPY = {
    [EVENT_HUB_UPLOAD_KIND.BANNER]: {
        title: 'Upload a banner image',
        dims: '1920 × 1080 px recommended (min. 1920 × 640), JPG or PNG, max 5 MB',
    },
    [EVENT_HUB_UPLOAD_KIND.LOGO]: {
        title: 'Upload the event logo',
        dims: 'JPG or PNG, max 2 MB',
    },
    [EVENT_HUB_UPLOAD_KIND.PARTNER]: {
        title: 'Add a partner logo',
        dims: 'Max. 600 × 200 px (any ratio), PNG or JPG, max 2 MB',
    },
    [EVENT_HUB_UPLOAD_KIND.PHOTOS]: {
        title: 'Add photos',
        dims: 'JPG or PNG, max 2 MB per photo',
    },
}

/** 23px blue outlined × that removes a picked row (mock `.org-ico.s23`). */
const REMOVE_CONTROL_CLASS =
    's-focus-ring ml-auto flex size-[23px] shrink-0 cursor-pointer items-center justify-center rounded-[5px] border border-[#1672ec] bg-white text-[10px] text-[#1672ec] transition-colors hover:bg-[#1672ec] hover:text-white'

let nextEntryId = 0

/**
 * Reads one picked File into a row entry. The bitmap is only decoded for JPEG/PNG;
 * anything else is listed with a type refusal and never read.
 */
const readEntry = async (file, rule) => {
    const base = {
        id: `upload-${++nextEntryId}`,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        previewUrl: null,
        decoded: false,
        width: 0,
        height: 0,
    }
    let entry = base
    if (isAcceptedUploadType(file.type)) {
        const dimensions = await readImageDimensions(file)
        if (dimensions) {
            entry = { ...base, previewUrl: URL.createObjectURL(file), decoded: true, ...dimensions }
        }
    }
    return { ...entry, issue: getUploadFileIssue(entry, rule) }
}

/**
 * Shared organizer upload dialog (mock `#upDlg`): a drop zone (click, Enter/Space,
 * drag-drop) over a real file input, the requirements line for the kind, then one
 * row per picked file validated on selection — a refused file stays listed with its
 * reason in red and a × to drop it. "Upload" only enables with a valid file.
 *
 * `onUpload(files)` receives the valid File objects. Return a promise to keep the
 * dialog open (busy) until it settles; return nothing to close at once and let the
 * caller report progress elsewhere (the banner shows it inline on the hero).
 */
export default function EventHubUploadModal({ kind, title, onClose, onUpload }) {
    const { t } = useTranslation(TranslationNS.EVENT_PAGE)
    const rule = EVENT_HUB_UPLOAD_RULES[kind]
    const copy = KIND_COPY[kind]

    const inputRef = useRef(null)
    const dropRef = useRef(null)
    const [entries, setEntries] = useState([])
    const [isOver, setIsOver] = useState(false)
    const [isBusy, setIsBusy] = useState(false)

    // Object URLs back the thumbnails; release them all when the dialog goes.
    const entriesRef = useRef(entries)
    entriesRef.current = entries
    useEffect(
        () => () => entriesRef.current.forEach((entry) => entry.previewUrl && URL.revokeObjectURL(entry.previewUrl)),
        [],
    )

    const units = { kb: t('KB'), mb: t('MB') }

    const issueText = (issue) => {
        if (!issue) return ''
        const { params = {} } = issue
        switch (issue.code) {
            case UPLOAD_ISSUE.SIZE:
                return t('{{size}}: max {{max}}', {
                    size: formatUploadBytes(params.size, units),
                    max: formatUploadBytes(params.max, units),
                })
            case UPLOAD_ISSUE.MIN:
                return t('{{width}} × {{height}} px: at least {{minWidth}} × {{minHeight}} px', params)
            case UPLOAD_ISSUE.MAX:
                return t('{{width}} × {{height}} px: max {{maxWidth}} × {{maxHeight}} px', params)
            default:
                return t('JPG or PNG only')
        }
    }

    const addFiles = async (fileList) => {
        let files = Array.from(fileList || [])
        if (!files.length || isBusy) return
        if (!rule.multiple) files = files.slice(0, 1)
        const read = await Promise.all(files.map((file) => readEntry(file, rule)))
        setEntries((previous) => {
            if (rule.multiple) return [...previous, ...read]
            previous.forEach((entry) => entry.previewUrl && URL.revokeObjectURL(entry.previewUrl))
            return read
        })
    }

    const removeEntry = (id) =>
        setEntries((previous) => {
            const entry = previous.find((item) => item.id === id)
            if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl)
            return previous.filter((item) => item.id !== id)
        })

    const openPicker = () => {
        if (!isBusy) inputRef.current?.click()
    }

    const validFiles = getValidUploadFiles(entries, rule).map((entry) => entry.file)
    const rejectedCount = entries.filter((entry) => entry.issue).length

    const submit = async () => {
        if (!validFiles.length || isBusy) return
        const result = onUpload(validFiles)
        if (result && typeof result.then === 'function') {
            setIsBusy(true)
            try {
                await result
            } catch (error) {
                // The caller already reported it; keep the dialog open for a retry.
                setIsBusy(false)
                return
            }
        }
        onClose()
    }

    return (
        <EventHubDialog
            id="event-hub-upload-modal"
            title={title || t(copy.title)}
            zClass={EVENT_HUB_DIALOG_Z.UPLOAD}
            initialFocusRef={dropRef}
            onClose={isBusy ? () => {} : onClose}
        >
            <div
                ref={dropRef}
                role="button"
                tabIndex={0}
                aria-label={t('Choose a file')}
                onClick={openPicker}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        openPicker()
                    }
                }}
                onDragEnter={(event) => {
                    event.preventDefault()
                    setIsOver(true)
                }}
                onDragOver={(event) => {
                    event.preventDefault()
                    setIsOver(true)
                }}
                onDragLeave={() => setIsOver(false)}
                onDrop={(event) => {
                    event.preventDefault()
                    setIsOver(false)
                    addFiles(event.dataTransfer.files)
                }}
                className={`s-focus-ring flex min-h-40 cursor-pointer flex-col items-center justify-center gap-2 rounded-[10px] border border-dashed bg-[#f7f7f9] p-5 text-center text-sm leading-[1.4] transition-colors hover:border-[#1672ec] hover:text-[#272727] ${
                    rejectedCount
                        ? 'border-[#b93a2c] text-[#b93a2c]'
                        : isOver
                        ? 'border-[#1672ec] text-[#272727]'
                        : 'border-[#dbdadf] text-[#272727]/70'
                }`}
            >
                <FileImageOutlined className="text-[28px] text-[#272727]/40" />
                <span>
                    <b className="font-semibold text-[#272727]">{t('Drag & drop your file here')}</b>{' '}
                    <u className="text-[#1672ec]">{t('or browse')}</u>
                </span>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    multiple={!!rule.multiple}
                    className="hidden"
                    onChange={(event) => {
                        addFiles(event.target.files)
                        event.target.value = ''
                    }}
                />
            </div>

            <p className="m-0 text-center text-xs text-[#272727]/50">{t(copy.dims)}</p>

            {entries.length > 0 && (
                <div className="flex flex-col gap-2">
                    {entries.map((entry) => (
                        <div
                            key={entry.id}
                            className={`flex min-w-0 items-center gap-3 rounded-[5px] border px-2.5 py-2 text-[13px] ${
                                entry.issue ? 'border-[#b93a2c]' : 'border-[#dbdadf]'
                            }`}
                        >
                            {entry.previewUrl ? (
                                <img
                                    src={entry.previewUrl}
                                    alt=""
                                    className="h-10 w-16 shrink-0 rounded-[5px] bg-[#f7f7f9] object-cover"
                                />
                            ) : (
                                <span
                                    aria-hidden
                                    className="flex h-10 w-16 shrink-0 items-center justify-center rounded-[5px] bg-[#f7f7f9] text-[20px] text-[#272727]/40"
                                >
                                    <FileImageOutlined />
                                </span>
                            )}
                            <span className="min-w-0 flex-1">
                                <b className="block truncate font-semibold text-[#272727]">{entry.name}</b>
                                <small className="text-[#272727]/50">
                                    {entry.decoded ? `${entry.width} × ${entry.height} px · ` : ''}
                                    {formatUploadBytes(entry.size, units)}
                                </small>
                                {entry.issue && (
                                    <span className="block text-xs text-[#b93a2c]">{issueText(entry.issue)}</span>
                                )}
                            </span>
                            <button
                                type="button"
                                disabled={isBusy}
                                aria-label={t('Remove this file')}
                                onClick={() => removeEntry(entry.id)}
                                className={REMOVE_CONTROL_CLASS}
                            >
                                <CloseOutlined />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {rule.multiple && rejectedCount > 0 && (
                <p role="alert" className="m-0 text-[13px] text-[#b93a2c]">
                    {t('{{rejected}} of {{total}} files rejected', { rejected: rejectedCount, total: entries.length })}
                </p>
            )}

            <EventHubDialogActions>
                <button
                    type="button"
                    disabled={isBusy}
                    onClick={onClose}
                    className={`${EVENT_HUB_DIALOG_BUTTON_CLASS} ${EVENT_HUB_DIALOG_GHOST_CLASS}`}
                >
                    {t('Cancel')}
                </button>
                <button
                    type="button"
                    disabled={!validFiles.length || isBusy}
                    onClick={submit}
                    className={`${EVENT_HUB_DIALOG_BUTTON_CLASS} ${EVENT_HUB_DIALOG_PRIMARY_CLASS}`}
                >
                    {isBusy ? t('Uploading...') : t('Upload')}
                    {isBusy && <LoadingOutlined />}
                </button>
            </EventHubDialogActions>
        </EventHubDialog>
    )
}
